<template>
  <div id="videoembed" :class="{ 'is-fill': fill }">
    <div v-if="videoPlatform === 'vimeo'" class="video-fill">
      <div class="vimeo-container" :style="containerStyle">
        <iframe
          ref="iframe"
          :src="`https://player.vimeo.com/video/${src}?badge=0&autopause=0&player_id=0&app_id=1&autoplay=${
            autoplay || playOnHover ? 1 : 0
          }&controls=${controls ? 1 : 0}&loop=${loop ? 1 : 0}`"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
          :title="title"
        ></iframe>
      </div>
    </div>
    <div v-else-if="videoPlatform === 'youtube'" class="video-fill">
      <div class="vid-div" :style="containerStyle">
        <iframe
          :id="playerId"
          class="responsive-iframe"
          :src="`https://www.youtube.com/embed/${src}?rel=0&enablejsapi=1&autoplay=${
            autoplay || playOnHover ? 1 : 0
          }&controls=${controls ? 1 : 0}&loop=${loop ? 1 : 0}${
            loop ? '&playlist=' + src : ''
          }&modestbranding=1&showinfo=0`"
          frameborder="0"
          allow="autoplay; encrypted-media"
        ></iframe>
        <div v-if="!controls" class="overlay"></div>
      </div>
    </div>
    <div v-else>No source</div>
  </div>
</template>

<script>
export default {
  name: "VideoEmbed",
  props: {
    src: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: "Video",
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    controls: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    playOnHover: {
      type: Boolean,
      default: false,
    },
    // CSS aspect-ratio used when NOT filling (e.g. "1 / 1", "16 / 9").
    aspectRatio: {
      type: String,
      default: "1 / 1",
    },
    // When true, the embed stretches to fill its parent (the parent must
    // have a defined size). Used by MediaGallery's fixed-size stage.
    fill: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    player: null,
    youtubePlayer: null,
    currentTime: 0,
    isPlaying: false,
    timeUpdateInterval: null,
  }),
  computed: {
    videoPlatform() {
      return /^[0-9]+$/.test(this.src) ? "vimeo" : "youtube";
    },
    // unique per component instance to avoid duplicate-id collisions when the
    // same video appears more than once on a page
    playerId() {
      return `youtube-player-${this.src}-${this._uid}`;
    },
    containerStyle() {
      return this.fill
        ? { width: "100%", height: "100%" }
        : { aspectRatio: this.aspectRatio };
    },
  },
  watch: {
    playOnHover(newVal) {
      if (this.videoPlatform === "vimeo" && this.player) {
        if (newVal) {
          this.playVimeo();
        } else {
          this.pauseVimeo();
        }
      } else if (this.videoPlatform === "youtube" && this.youtubePlayer) {
        if (newVal) {
          this.playYouTube();
        } else {
          this.pauseYouTube();
        }
      }
    },
  },
  mounted() {
    if (this.videoPlatform === "vimeo") {
      this.loadVimeoPlayer();
    } else if (this.videoPlatform === "youtube") {
      this.loadYouTubePlayer();
    }
  },
  beforeDestroy() {
    this.stopYouTubeTimeUpdate();
    if (this.player) {
      try {
        this.player.off("play");
        this.player.off("ended");
        this.player.off("timeupdate");
      } catch (error) {
        console.error("Error tearing down Vimeo player:", error);
      }
      this.player = null;
    }
    if (this.youtubePlayer) {
      if (typeof this.youtubePlayer.stopVideo === "function") {
        try {
          this.youtubePlayer.stopVideo();
          this.youtubePlayer.destroy();
        } catch (error) {
          console.error("Error destroying YouTube player:", error);
        }
      }
      this.youtubePlayer = null;
    }
  },
  methods: {
    loadVimeoPlayer() {
      if (!window.Vimeo) {
        const script = document.createElement("script");
        script.src = "https://player.vimeo.com/api/player.js";
        script.onload = () => this.initializeVimeoPlayer();
        document.body.appendChild(script);
      } else {
        this.initializeVimeoPlayer();
      }
    },
    initializeVimeoPlayer() {
      // guard against init after the component was already destroyed
      if (!this.$refs.iframe) return;
      this.player = new window.Vimeo.Player(this.$refs.iframe);
      this.player.on("play", () => {
        this.isPlaying = true;
        this.$emit("play");
      });
      this.player.on("ended", () => {
        this.isPlaying = false;
        this.currentTime = 0;
        this.$emit("ended");
      });
      this.player.on("timeupdate", (data) => {
        this.currentTime = data.seconds;
      });
    },
    loadYouTubePlayer() {
      if (!window.YT) {
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.onload = () => {
          window.YT.ready(() => {
            this.initializeYouTubePlayer();
          });
        };
        document.body.appendChild(script);
      } else if (window.YT && window.YT.Player) {
        window.YT.ready(() => {
          this.initializeYouTubePlayer();
        });
      }
    },
    initializeYouTubePlayer() {
      // the iframe may have been removed if we switched away quickly
      if (!document.getElementById(this.playerId)) return;
      this.youtubePlayer = new window.YT.Player(this.playerId, {
        events: {
          onReady: () => {
            // Initial setup if needed
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              this.isPlaying = true;
              this.$emit("play");
              this.startYouTubeTimeUpdate();
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              this.isPlaying = false;
              this.stopYouTubeTimeUpdate();
            } else if (event.data === window.YT.PlayerState.ENDED) {
              this.isPlaying = false;
              this.currentTime = 0;
              this.$emit("ended");
              this.stopYouTubeTimeUpdate();
            }
          },
          onError: (error) => {
            console.error("YouTube Player Error:", error);
          },
        },
      });
    },
    playVimeo() {
      if (this.player) {
        this.player.setCurrentTime(this.currentTime).then(() => {
          this.player.play();
        });
      }
    },
    pauseVimeo() {
      if (this.player) {
        this.player.getCurrentTime().then((time) => {
          this.currentTime = time;
          this.player.pause();
        });
      }
    },
    playYouTube() {
      if (
        this.youtubePlayer &&
        typeof this.youtubePlayer.seekTo === "function"
      ) {
        this.youtubePlayer.seekTo(this.currentTime, true);
        this.youtubePlayer.playVideo();
      }
    },
    pauseYouTube() {
      if (
        this.youtubePlayer &&
        typeof this.youtubePlayer.getCurrentTime === "function"
      ) {
        this.currentTime = this.youtubePlayer.getCurrentTime();
        this.youtubePlayer.pauseVideo();
      }
    },
    startYouTubeTimeUpdate() {
      if (!this.timeUpdateInterval) {
        this.timeUpdateInterval = setInterval(() => {
          if (this.youtubePlayer && this.isPlaying) {
            this.currentTime = this.youtubePlayer.getCurrentTime();
          }
        }, 1000);
      }
    },
    stopYouTubeTimeUpdate() {
      if (this.timeUpdateInterval) {
        clearInterval(this.timeUpdateInterval);
        this.timeUpdateInterval = null;
      }
    },
  },
};
</script>

<style scoped>
#videoembed,
.video-fill {
  width: 100%;
}

/* fill mode: propagate height down so the iframe fills the parent box */
#videoembed.is-fill,
#videoembed.is-fill .video-fill {
  height: 100%;
}

/* Vimeo */
.vimeo-container {
  position: relative;
  width: 100%;
}
.vimeo-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* YouTube */
.vid-div {
  position: relative;
  width: 100%;
  overflow: hidden;
}
.responsive-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 10;
  pointer-events: auto;
}
</style>
